import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, Calendar, Clock, Users, Calculator, ArrowRight, HelpCircle } from "lucide-react";
import { BrandedHomeLink } from "@/components/BrandedHomeLink";
import { setAuthToken, clearAuthToken, getAuthToken } from "@/App";
import { formatApiError } from "@/utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const showDevBypass =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_DEV_BYPASS === "true";

function nextPathFromSearch(searchParams) {
  const redirectParam = searchParams.get("redirect");
  if (redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")) {
    return redirectParam;
  }
  return "/dashboard";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [consuming, setConsuming] = useState(false);
  const [email, setEmail] = useState("");
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    const emailToken = searchParams.get("email_token");
    if (emailToken) {
      let cancelled = false;
      const consume = async () => {
        setConsuming(true);
        setChecking(false);
        try {
          const res = await axios.post(`${API}/auth/email/consume`, { token: emailToken });
          if (cancelled) return;
          const sessionToken = res.data?.session_token;
          if (!sessionToken) {
            throw new Error("No session was returned");
          }
          setAuthToken(sessionToken);
          sessionStorage.removeItem("just_logged_out");
          window.history.replaceState({}, "", "/login");
          const storedRedirect = sessionStorage.getItem("login_redirect");
          if (storedRedirect) {
            sessionStorage.removeItem("login_redirect");
          }
          const dest =
            (typeof res.data?.next === "string" && res.data.next.startsWith("/") && !res.data.next.startsWith("//")
              ? res.data.next
              : null) ||
            storedRedirect ||
            "/dashboard";
          navigate(dest, { state: { user: res.data }, replace: true });
        } catch (err) {
          if (cancelled) return;
          clearAuthToken();
          toast.error(formatApiError(err, "This sign-in link is invalid or has expired. Request a new one."));
          window.history.replaceState({}, "", "/login");
          setConsuming(false);
        }
      };
      consume();
      return () => {
        cancelled = true;
      };
    }

    const loggedOut = searchParams.get("logged_out");
    if (loggedOut === "true") {
      sessionStorage.setItem("just_logged_out", "true");
      setChecking(false);
      window.history.replaceState({}, "", "/login");
      return;
    }

    const justLoggedOut = sessionStorage.getItem("just_logged_out");
    if (justLoggedOut) {
      sessionStorage.removeItem("just_logged_out");
      setChecking(false);
      return;
    }

    const token = getAuthToken();
    if (token) {
      axios
        .get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => navigate("/dashboard"))
        .catch(() => {
          clearAuthToken();
          setChecking(false);
        });
    } else {
      setChecking(false);
    }
  }, [navigate, searchParams]);

  const handleGoogleLogin = () => {
    if (!BACKEND_URL && process.env.NODE_ENV !== "production") {
      toast.error(
        "Set REACT_APP_BACKEND_URL (e.g. http://localhost:8000) in frontend/.env"
      );
      return;
    }
    setLoading(true);

    const next = nextPathFromSearch(searchParams);
    const returnOrigin = window.location.origin;
    const url =
      `${BACKEND_URL}/api/auth/google/start?next=${encodeURIComponent(next)}` +
      `&return_origin=${encodeURIComponent(returnOrigin)}`;
    window.location.href = url;
  };

  const handleEmailLink = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Enter your email address.");
      return;
    }
    if (!BACKEND_URL && process.env.NODE_ENV !== "production") {
      toast.error(
        "Set REACT_APP_BACKEND_URL (e.g. http://localhost:8000) in frontend/.env"
      );
      return;
    }
    setSendingLink(true);
    try {
      await axios.post(`${API}/auth/email/start`, {
        email: trimmed,
        next: nextPathFromSearch(searchParams),
        return_origin: window.location.origin,
      });
      setLinkSent(true);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send a sign-in link. Try again."));
    } finally {
      setSendingLink(false);
    }
  };

  if (checking || loading || consuming) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BrandedHomeLink
            to="/dashboard"
            logoClassName="w-16 h-16 animate-pulse"
            className="mx-auto justify-center mb-4"
          />
          <p className="text-muted-foreground">
            {consuming
              ? "Signing you in…"
              : loading
                ? "Redirecting to Google…"
                : "Checking session…"}
          </p>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Calendar, text: "Due dates skip weekends and Hawaii holidays" },
    { icon: Clock, text: "The next date can start when the last job is done" },
    { icon: Users, text: "A reminder, and a link your client can open" },
    { icon: Shield, text: "Your files stay encrypted" },
  ];

  const isSignup = searchParams.get("signup") === "true";

  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-card via-card to-primary/20 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />

        <div className="relative">
          <BrandedHomeLink to="/dashboard" logoClassName="w-10 h-10" className="mb-12">
            <span className="text-2xl font-bold text-foreground">TerraKronos</span>
          </BrandedHomeLink>

          <h1 className="text-4xl font-bold text-foreground mb-4">
            Never miss a Hawaii contract deadline
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            Weekends and Hawaii holidays are counted for you. Your client can see the same dates.
          </p>

          <div className="space-y-4">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 text-foreground/90">
                <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <Button
            variant="ghost"
            onClick={() => navigate("/learn")}
            className="text-muted-foreground hover:text-foreground gap-2"
          >
            See the tutorials
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/faq")}
            className="text-muted-foreground hover:text-foreground gap-2"
          >
            <HelpCircle className="w-4 h-4" />
            FAQ
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/tools")}
            className="text-muted-foreground hover:text-foreground gap-2"
          >
            <Calculator className="w-4 h-4" />
            Try Hawaii Calculators
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <BrandedHomeLink to="/dashboard" logoClassName="w-10 h-10">
              <span className="text-2xl font-bold text-foreground">TerraKronos</span>
            </BrandedHomeLink>
          </div>

          <div className="bg-card/50 border border-border rounded-2xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {isSignup ? "Create your account" : "Welcome"}
              </h2>
              <p className="text-muted-foreground">
                {isSignup
                  ? "Use Google or an email sign-in link. No password needed."
                  : "Sign in with Google or an email link"}
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleGoogleLogin}
                disabled={loading || sendingLink}
                className="w-full h-12 bg-background hover:bg-muted text-foreground font-medium flex items-center justify-center gap-3 border border-border"
              >
                Continue with Google
              </Button>
              {showDevBypass && (
                <Button
                  onClick={() => {
                    const tok = "dev_bypass_local";
                    setAuthToken(tok);
                    sessionStorage.setItem("bypass_mode", "true");
                    localStorage.setItem("bypass_mode", "true");
                    toast.success("Development bypass. Use with DEV_AUTH_BYPASS on the API.");
                    window.location.href = "/dashboard";
                  }}
                  variant="outline"
                  className="w-full h-12 border-2 border-green-500 text-green-400 hover:bg-green-950 font-medium"
                >
                  Bypass login (development)
                </Button>
              )}
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground">or</span>
              </div>
            </div>

            {linkSent ? (
              <div className="space-y-4" data-testid="email-link-sent">
                <p className="text-sm text-foreground/90 text-center">
                  Check your email. If that address can receive mail, we sent a sign-in link.
                  It expires in 15 minutes.
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setLinkSent(false);
                    setEmail("");
                  }}
                >
                  Use a different email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleEmailLink} className="space-y-3">
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 bg-background"
                  disabled={sendingLink}
                  data-testid="email-sign-in-input"
                />
                <Button
                  type="submit"
                  disabled={sendingLink}
                  className="w-full h-12"
                  data-testid="email-sign-in-submit"
                >
                  {sendingLink ? "Sending…" : "Email me a sign-in link"}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-foreground/90">
                By signing in, you agree to our{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-foreground/90 mb-4">
              Free plan includes 5 transactions
            </p>
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="link"
                onClick={() => navigate("/faq")}
                className="text-primary hover:text-primary"
              >
                Questions? Read the FAQ
              </Button>
              <Button
                variant="link"
                onClick={() => navigate("/")}
                className="text-primary hover:text-primary"
              >
                Learn more about TerraKronos
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
